import { memo } from "react";
import { AddIcon, ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Image,
  Select,
  Text,
} from "@chakra-ui/react";
import { AuthInput } from "components/auth/AuthInput";
import {
  IconCardDiscover,
  IconCardExpress,
  IconCardMaster,
  IconCardVisa,
} from "components/icons/Icons";
import { ErrorAlert } from "views/auth/signIn/components/ErrorAlert";
import deleteIcon from "assets/img/customer/delete-icon.svg";

interface TeamMember {
  email: string;
  role: string;
  emailError?: string;
}

interface Step4FinalizeAccountProps {
  teamMembers: TeamMember[];
  paymentMethod: string;
  cardHolderName: string;
  cardBrand: string;
  isCardNumberFocused: boolean;
  isCardExpiryFocused: boolean;
  isCardCvcFocused: boolean;
  paymentError: string | null;
  teamMemberError: string | null;
  onTeamMemberChange: (index: number, field: "email" | "role", value: string) => void;
  onAddTeamMember: () => void;
  onRemoveTeamMember: (index: number) => void;
  onPaymentMethodChange: (method: string) => void;
  onChangeCardHolderName: (value: string) => void;
  onComplete: () => void;
  onBack: () => void;
  onSkip: () => void;
  isSavingCardInfo: boolean;
}

export const FinalizeAccount = memo(function FinalizeAccount({
  teamMembers,
  paymentMethod,
  cardHolderName,
  cardBrand,
  isCardNumberFocused,
  isCardExpiryFocused,
  isCardCvcFocused,
  paymentError,
  teamMemberError,
  onTeamMemberChange,
  onAddTeamMember,
  onRemoveTeamMember,
  onPaymentMethodChange,
  onChangeCardHolderName,
  onComplete,
  onBack,
  onSkip,
  isSavingCardInfo,
}: Step4FinalizeAccountProps) {
  return (
    <Flex direction="column" alignItems="center" w="100%">
      <Heading
        fontSize="24px"
        fontWeight="600"
        fontFamily="'General Sans', sans-serif"
        w="100%"
        color="#000000"
        textAlign="center"
        pb="24px"
        borderBottom="1px solid #E6ECF3"
      >
        Finalize your account
        
      </Heading>

      <Text mt="24px" fontSize="18px" fontWeight="600" mb="24px" alignSelf="flex-start">
        Invite team members by email
      </Text>

      {teamMemberError && (
        <Box w="100%" mb="16px">
          <ErrorAlert iconColor="#B91C1C" message={teamMemberError} icon />
        </Box>
      )}

      <Box w="100%" mb="24px">
        {teamMembers.map((member, index) => (
          <Flex
            key={index}
            mb="24px"
            gap="16px"
            alignItems={{ base: "stretch", md: "center" }}
            flexDirection={{ base: "column", md: "row" }}
          >
            <FormControl flex="1" w={{ base: "100%", md: "auto" }}>
              <FormLabel
                htmlFor={`team_member_email_${index}`}
                fontSize="14px"
                fontWeight="400"
                color="#0B0E10"
                mb="8px"
              >
                Email
              </FormLabel>
              <AuthInput
                role="customer"
                chakraVariant="authCustomerNew"
                type="email"
                id={`team_member_email_${index}`}
                placeholder="Email"
                value={member.email}
                border="1px solid #7C8FA3"
                onChange={(e) => {
                  onTeamMemberChange(index, "email", e.target.value);
                }}
                isInvalid={!!member.emailError}
              />
            </FormControl>
            <FormControl flex="1" w={{ base: "100%", md: "auto" }}>
              <FormLabel
                htmlFor={`team_member_role_${index}`}
                fontSize="14px"
                fontWeight="400"
                color="#0B0E10"
                mb="8px"
              >
                Role
              </FormLabel>
              <Select
                id={`team_member_role_${index}`}
                value={member.role}
                onChange={(e) =>
                  onTeamMemberChange(index, "role", e.target.value)
                }
                h="48px"
                borderRadius="12px"
                borderWidth="1px"
                borderColor="#7C8FA3"
                fontSize="14px"
                lineHeight="135%"
                textAlign="left"
                color="#0B0E10"
                bg="white"
                boxShadow="none"
                icon={<ChevronDownIcon />}
                iconColor="#0B0E10"
                iconSize="20px"
                sx={{
                  icon: {
                    right: "16px",
                  },
                }}
                _focus={{
                  borderColor: "#3A7BFF",
                  boxShadow: "none",
                }}
                _hover={{
                  borderColor: "#7C8FA3",
                }}
                cursor="pointer"
              >
                <option value="Team member">Team member</option>
                <option value="Admin">Admin</option>
              </Select>
            </FormControl>
            <Flex justifyContent={{ base: "flex-end", md: "center" }}>
              <Image
                src={deleteIcon}
                w="24px"
                h="24px"
                mt={{base:"0px", md:"22px"}}
                cursor="pointer"
                onClick={() => onRemoveTeamMember(index)}
              />
            </Flex>
          </Flex>
        ))}

        <Button
          bg="white"
          borderRadius="24px"
          h="48px"
          w="175px"
          fontSize="16px"
          fontWeight="600"
          color="#4285F4"
          border="1px solid #3A7BFF"
          leftIcon={<AddIcon w="14px" h="14px" />}
          _hover={{
            bgColor: "#3A7BFF",
            color: "#FFF",
          }}
          _active={{
            bgColor: "#FFFFFF",
            color: "#3A7BFF",
          }}
          onClick={onAddTeamMember}
        >
          Add another
        </Button>
      </Box>

      <Divider mb="24px" />

      <Text fontSize="18px" fontWeight="600" mb="24px" alignSelf="flex-start">
        Add a payment method (optional)
      </Text>

      {paymentError && (
        <Box w="100%" mb="16px">
          <ErrorAlert iconColor="#B91C1C" message={paymentError} icon />
        </Box>
      )}

      <Box w="100%" mb="32px">
        <Box
          borderRadius="16px"
          border="1px solid #E9E9E9"
          p="16px"
          bg="#FFFFFF"
        >
          {/* Card Number Field */}
          <FormControl mb="16px">
            <FormLabel fontSize="14px" fontWeight="400" color="#000000" mb="16px">
              Card number
            </FormLabel>
            <Box position="relative">
              <Box
                padding="12px 16px"
                paddingRight="140px"
                borderRadius="12px"
                border={
                  isCardNumberFocused
                    ? "1px solid #3A7BFF"
                    : "1px solid #E9E9E9"
                }
                bg="white"
                position="relative"
                h="48px"
                display="flex"
                alignItems="center"
                color="#0B0E10"
                fontSize="14px"
                boxShadow="none"
              >
                <Box id="signup-business-card-number-element" w="100%"></Box>
              </Box>
              {/* Card Brand Icons */}
              <Flex
                position="absolute"
                right="12px"
                top="50%"
                transform="translateY(-50%)"
                gap="4px"
                alignItems="center"
              >
                <Box opacity={cardBrand === "visa" || cardBrand === "" ? 1 : 0.3}>
                  <IconCardVisa w="28px" h="18px" />
                </Box>
                <Box opacity={cardBrand === "amex" || cardBrand === "" ? 1 : 0.3}>
                  <IconCardExpress w="28px" h="18px" />
                </Box>
                <Box opacity={cardBrand === "discover" || cardBrand === "" ? 1 : 0.3}>
                  <IconCardDiscover w="28px" h="18px" />
                </Box>
                <Box opacity={cardBrand === "mastercard" || cardBrand === "" ? 1 : 0.3}>
                  <IconCardMaster w="28px" h="18px" />
                </Box>
              </Flex>
            </Box>
          </FormControl>

          {/* Name on Card Field */}
          <FormControl mb="16px">
            <FormLabel fontSize="14px" fontWeight="400" color="#0B0E10" mb="8px">
              Name on card
            </FormLabel>
            <AuthInput
              role="customer"
              chakraVariant="authCustomerNew"
              placeholder="John Doe"
              value={cardHolderName}
              onChange={(e) => onChangeCardHolderName(e.target.value)}
            />
          </FormControl>

          {/* Expiration Date and CVV */}
          <Flex gap="12px">
            <FormControl flex="1">
              <FormLabel fontSize="14px" fontWeight="400" color="#0B0E10" mb="8px">
                Expiration date
              </FormLabel>
              <Box
                padding="12px 16px"
                borderRadius="12px"
                border={
                  isCardExpiryFocused
                    ? "1px solid #3A7BFF"
                    : "1px solid #E9E9E9"
                }
                bg="white"
                h="48px"
                display="flex"
                alignItems="center"
                color="#0B0E10"
                fontSize="14px"
                boxShadow="none"
              >
                <Box id="signup-business-card-expiry-element" w="100%" minH="24px"></Box>
              </Box>
            </FormControl>
            <FormControl flex="1">
              <FormLabel fontSize="14px" fontWeight="400" color="#0B0E10" mb="8px">
                CVV
              </FormLabel>
              <Box
                padding="12px 16px"
                borderRadius="12px"
                border={
                  isCardCvcFocused
                    ? "1px solid #3A7BFF"
                    : "1px solid #E9E9E9"
                }
                bg="white"
                h="48px"
                display="flex"
                alignItems="center"
                color="#0B0E10"
                fontSize="14px"
                boxShadow="none"
              >
                <Box id="signup-business-card-cvc-element" w="100%" minH="24px"></Box>
              </Box>
            </FormControl>
          </Flex>
        </Box>
      </Box>

      <Button
        w="100%"
        h="48px"
        fontSize="16px"
        bgColor="#3A7BFF"
        fontWeight="600"
        color="#FFFFFF"
        borderRadius="25px"
        border="1px solid #3A7BFF"
        mb="12px"
        _hover={{
          bgColor: "#FFFFFF",
          color: "#3A7BFF",
        }}
        _active={{
          bgColor: "#FFFFFF",
          color: "#3A7BFF",
        }}
        _loading={{ pointerEvents: "none" }}
        onClick={onComplete}
        isLoading={isSavingCardInfo}
      >
        Complete setup
      </Button>

      <Button
        w="100%"
        h="48px"
        bgColor="white"
        color="#3A7BFF"
        borderRadius="24px"
        fontSize="14px"
        fontWeight="600"
        border="1px solid #3A7BFF"
        mb="12px"
        _hover={{
          bgColor: "#3A7BFF",
          color: "#FFF",
        }}
        _active={{
          bgColor: "#FFFFFF",
          color: "#3A7BFF",
        }}
        onClick={onBack}
      >
        Back
      </Button>

      <Text
        fontSize="14px"
        color="#4285F4"
        cursor="pointer"
        fontWeight="500"
        onClick={onSkip}
      >
        Skip
      </Text>
    </Flex>
  );
});
